import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Key, CreditCard, Settings, X } from 'lucide-react';
import { cn } from '@/utils';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();

  // 文档链接：开发环境使用 VitePress 开发服务器，生产环境使用构建后的文档
  // 支持通过 VITE_DOCS_URL 环境变量自定义文档地址
  const docsUrl = import.meta.env.VITE_DOCS_URL ||
    (import.meta.env.DEV ? 'http://localhost:5174/docs/' : '/docs/');

  const menuItems = [
    {
      path: '/dashboard/api-keys',
      label: 'API 密钥',
      icon: Key,
    },
    {
      path: '/dashboard/subscription',
      label: '订阅管理',
      icon: CreditCard,
    },
    {
      path: '/dashboard/settings',
      label: '账户设置',
      icon: Settings,
    },
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-16 left-0 z-50 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 transition-transform lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Close button for mobile */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
            <span className="font-semibold text-gray-900">菜单</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-warm-sand/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onClose()}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-warm-sand/30'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Bottom section */}
          <div className="p-4 border-t border-gray-200">
            <div className="bg-primary-50 rounded-lg p-4">
              <p className="text-sm font-medium text-primary-900 mb-2">
                需要帮助?
              </p>
              <p className="text-xs text-primary-700 mb-3">
                查看文档或联系支持团队
              </p>
              <a
                href={docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                查看文档 →
              </a>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
